import { logger } from "../utils/logger";
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api } from '../services/api';
import { NexusStatus } from '../types/nexus';
import axios from 'axios';
import { useAuth } from './AuthContext';

interface NexusContextType {
    nexusStatus: NexusStatus | null;
    loading: boolean;
    refreshing: boolean;
    error: string | null;
    refreshNexusStatus: () => Promise<void>;
}

const NexusContext = createContext<NexusContextType>({
    nexusStatus: null,
    loading: false,
    refreshing: false,
    error: null,
    refreshNexusStatus: async () => {},
});

export const NexusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [nexusStatus, setNexusStatus] = useState<NexusStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { isAuthenticated } = useAuth();

    const fetchNexusStatus = useCallback(async () => {
        if (!isAuthenticated) return;
        const useBackgroundRefresh = hasLoadedOnce;
        try {
            if (useBackgroundRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }
            setError(null);
            const response = await api.get('/api/v1/nexus/locations');
            // Transform the response data to match NexusStatus interface
            const transformedData: NexusStatus = {
                status: 'active',
                active_nexus: response.data.filter((loc: any) => loc.is_active).length,
                states: response.data.map((loc: any) => loc.state),
                last_updated: new Date().toISOString(),
                compliance_score: 100, // This would need to be calculated based on actual compliance data
                pending_filings: 0, // This would need to be calculated based on actual filing data
                total_locations: response.data.length
            };
            setNexusStatus(transformedData);
            if (!hasLoadedOnce) {
                setHasLoadedOnce(true);
            }
        } catch (err) {
            if (axios.isAxiosError(err)) {
                if (err.response?.status === 403) {
                    setError('Authentication required. Please log in again.');
                } else if (err.response?.status === 404) {
                    setError('Nexus locations endpoint not found. Please check API configuration.');
                } else {
                    setError(`Failed to fetch nexus status: ${err.message}`);
                }
            } else {
                setError('An unexpected error occurred while fetching nexus status');
            }
            logger.error('Error fetching nexus status:', err);
        } finally {
            if (useBackgroundRefresh) {
                setRefreshing(false);
            } else {
                setLoading(false);
            }
        }
    }, [hasLoadedOnce, isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated || hasLoadedOnce) {
            return;
        }
        void fetchNexusStatus();
    }, [isAuthenticated, hasLoadedOnce, fetchNexusStatus]);

    // Remove useEffect that fetches nexus status on isAuthenticated
    // Only export fetchNexusStatus and context, do not auto-fetch on mount

    return (
        <NexusContext.Provider
            value={{
                nexusStatus,
                loading,
                refreshing,
                error,
                refreshNexusStatus: fetchNexusStatus,
            }}
        >
            {children}
        </NexusContext.Provider>
    );
};

export const useNexus = () => useContext(NexusContext); 
